import {useCallback, useMemo, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {MEMBER_SEARCH_PAGE_SIZE} from '@common/constants/pagination';
import type {RouterInput} from '@common-web/lib/trpc';

/**
 * Single source of truth for the search input shape — inferred from the tRPC router so
 * any schema change on the backend (new filter, renamed field, etc.) surfaces here as a
 * type error instead of silently drifting.
 */
type MemberSearchInput = RouterInput['vba']['members']['search'];

export {MEMBER_SEARCH_PAGE_SIZE};

export type MemberSearchSortKey = NonNullable<MemberSearchInput['sortKey']>;

/** Runtime list — kept in sync with `MemberSearchSortKey` via `satisfies`. */
export const MEMBER_SEARCH_SORT_KEYS = [
  'lastName',
  'dateOfBirth',
  'activeDate',
  'deactivatedDate',
] as const satisfies readonly MemberSearchSortKey[];

/** `''` is the UI sentinel for "all"; the others mirror the backend enum. */
export type MemberSearchActiveStatus = '' | NonNullable<MemberSearchInput['activeStatusQuery']>;

/** VBA relationship codes used as canonical filter values. */
export const RELATIONSHIP_OPTIONS = [
  {value: '01', label: 'Primary'},
  {value: '02', label: 'Spouse'},
  {value: '03', label: 'Child'},
  {value: '19', label: 'Dependent'},
] as const;

export type MemberSearchUrlState = {
  /** 1-based results page (synced to URL `page`). */
  page: number;
  q: string;
  activeStatus: MemberSearchActiveStatus;
  /** VBA relationship code filter (01, 02, 03, 19) — empty string means "all". */
  relationship: string;
  /** Client (tenant) id filter — empty string means "all". Internal portal only. */
  clientId: string;
  /** When true, hide VIP (CleverBenefits-only) members from the results. */
  excludeVip: boolean;
  firstName: string;
  lastName: string;
  zipCode: string;
  email: string;
  phoneNumber: string;
  groupName: string;
  /**
   * Exact VBA Group_ID "pin" (synced to URL `groupId`). When set, results are
   * restricted to this single group via the backend's access-checked top-level
   * `groupId` filter, and `groupName` is treated purely as the display label for
   * the on-page pinned-group banner (not sent as a separate `groupName` filter).
   */
  groupId: string;
  /**
   * Administrator code pinning `groupId` to one external TPA (synced to URL
   * `externalTpa`). A TPA group id is only unique within its administrator, so the
   * pair is what names one group; empty means the pin is a VBA group.
   */
  externalTpa: string;
  /** `YYYY-MM-DD` (ISO) */
  dateOfBirth: string;
  /** Inclusive enrollment effective-start range bounds (`YYYY-MM-DD`); empty means unbounded. */
  effectiveStartFrom: string;
  effectiveStartTo: string;
  /** Inclusive enrollment effective-end range bounds (`YYYY-MM-DD`); empty means unbounded. */
  effectiveEndFrom: string;
  effectiveEndTo: string;
  /** Restrict to members who have an app-user account set up. */
  hasAppAccount: boolean;
  /** Restrict to members whose app-user account has a verified email. */
  hasVerifiedEmail: boolean;
  /**
   * Restrict to members who do NOT have an app-user account. Mutually exclusive
   * with `hasAppAccount`/`hasVerifiedEmail` (maps to API `hasAppAccount: false`;
   * if both are set, `hasAppAccount` wins).
   */
  notRegistered: boolean;
  sortKey: MemberSearchSortKey;
  sortDir: 'asc' | 'desc';
};

export type MemberSearchAdvancedSlice = Pick<
  MemberSearchUrlState,
  | 'firstName'
  | 'lastName'
  | 'zipCode'
  | 'email'
  | 'phoneNumber'
  | 'groupName'
  | 'dateOfBirth'
  | 'effectiveStartFrom'
  | 'effectiveStartTo'
  | 'effectiveEndFrom'
  | 'effectiveEndTo'
>;

function parseSortKey(raw: string | null): MemberSearchSortKey {
  if (raw && (MEMBER_SEARCH_SORT_KEYS as readonly string[]).includes(raw)) {
    return raw as MemberSearchSortKey;
  }
  return 'lastName';
}

const ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'active',
  'inactive',
  'terminated',
  'openEnrollment',
  'gapCoverage',
]);

function parseActiveStatus(raw: string | null): MemberSearchActiveStatus {
  if (raw !== null && ACTIVE_STATUSES.has(raw)) return raw as MemberSearchActiveStatus;
  return '';
}

function parsePage(raw: string | null): number {
  const n = Number.parseInt(raw ?? '1', 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

const VALID_RELATIONSHIP_CODES: Set<string> = new Set(RELATIONSHIP_OPTIONS.map((o) => o.value));

function parseRelationship(raw: string | null): string {
  if (raw && VALID_RELATIONSHIP_CODES.has(raw)) return raw;
  return '';
}

export const DEFAULT_MEMBER_SEARCH_STATE: MemberSearchUrlState = {
  page: 1,
  q: '',
  activeStatus: '',
  relationship: '',
  clientId: '',
  excludeVip: false,
  firstName: '',
  lastName: '',
  zipCode: '',
  email: '',
  phoneNumber: '',
  groupName: '',
  groupId: '',
  externalTpa: '',
  dateOfBirth: '',
  effectiveStartFrom: '',
  effectiveStartTo: '',
  effectiveEndFrom: '',
  effectiveEndTo: '',
  hasAppAccount: false,
  hasVerifiedEmail: false,
  notRegistered: false,
  sortKey: 'lastName',
  sortDir: 'asc',
} as const;

export function parseMemberSearchUrlState(searchParams: URLSearchParams): MemberSearchUrlState {
  const state: MemberSearchUrlState = {
    ...DEFAULT_MEMBER_SEARCH_STATE,
    page: parsePage(searchParams.get('page')),
    q: searchParams.get('q') ?? '',
    activeStatus: parseActiveStatus(searchParams.get('activeStatus')),
    relationship: parseRelationship(searchParams.get('relationship')),
    clientId: searchParams.get('clientId') ?? '',
    excludeVip: searchParams.get('excludeVip') === '1',
    firstName: searchParams.get('firstName') ?? '',
    lastName: searchParams.get('lastName') ?? '',
    zipCode: searchParams.get('zipCode') ?? '',
    email: searchParams.get('email') ?? '',
    phoneNumber: searchParams.get('phoneNumber') ?? '',
    groupName: searchParams.get('groupName') ?? '',
    groupId: searchParams.get('groupId') ?? '',
    externalTpa: searchParams.get('externalTpa') ?? '',
    dateOfBirth: searchParams.get('dateOfBirth') ?? '',
    effectiveStartFrom: searchParams.get('effectiveStartFrom') ?? '',
    effectiveStartTo: searchParams.get('effectiveStartTo') ?? '',
    effectiveEndFrom: searchParams.get('effectiveEndFrom') ?? '',
    effectiveEndTo: searchParams.get('effectiveEndTo') ?? '',
    hasAppAccount: searchParams.get('hasAppAccount') === '1',
    hasVerifiedEmail: searchParams.get('hasVerifiedEmail') === '1',
    notRegistered: searchParams.get('notRegistered') === '1',
    sortKey: parseSortKey(searchParams.get('sortKey')),
    sortDir: searchParams.get('sortDir') === 'desc' ? 'desc' : 'asc',
  };

  return state;
}

export function serializeMemberSearchUrlState(s: MemberSearchUrlState): URLSearchParams {
  const p = new URLSearchParams();
  if (s.page > 1) p.set('page', String(s.page));
  if (s.q) p.set('q', s.q);
  if (s.activeStatus) p.set('activeStatus', s.activeStatus);
  if (s.relationship) p.set('relationship', s.relationship);
  if (s.clientId) p.set('clientId', s.clientId);
  if (s.excludeVip) p.set('excludeVip', '1');
  if (s.firstName) p.set('firstName', s.firstName);
  if (s.lastName) p.set('lastName', s.lastName);
  if (s.zipCode) p.set('zipCode', s.zipCode);
  if (s.email) p.set('email', s.email);
  if (s.phoneNumber) p.set('phoneNumber', s.phoneNumber);
  if (s.groupName) p.set('groupName', s.groupName);
  if (s.groupId) p.set('groupId', s.groupId);
  if (s.externalTpa) p.set('externalTpa', s.externalTpa);
  if (s.dateOfBirth) p.set('dateOfBirth', s.dateOfBirth);
  if (s.effectiveStartFrom) p.set('effectiveStartFrom', s.effectiveStartFrom);
  if (s.effectiveStartTo) p.set('effectiveStartTo', s.effectiveStartTo);
  if (s.effectiveEndFrom) p.set('effectiveEndFrom', s.effectiveEndFrom);
  if (s.effectiveEndTo) p.set('effectiveEndTo', s.effectiveEndTo);
  if (s.hasAppAccount) p.set('hasAppAccount', '1');
  if (s.hasVerifiedEmail) p.set('hasVerifiedEmail', '1');
  if (s.notRegistered) p.set('notRegistered', '1');
  if (s.sortKey !== 'lastName') p.set('sortKey', s.sortKey);
  if (s.sortDir !== 'asc') p.set('sortDir', s.sortDir);
  return p;
}

/**
 * Maps URL + debounced fields to `trpc.vba.members.search` input.
 * - **Quick search**: `nameQuery` only when `debouncedQ` is non-empty.
 * - **Status**: `activeStatusQuery` only when user selected a bucket; omit = all members.
 * - **Advanced**: only non-empty fields from `debouncedAdvanced` (debounce in the UI).
 *
 * `page` is excluded so the caller can swap pages without re-running the URL→input mapping.
 */
export function memberSearchUrlStateToApiInput(
  state: MemberSearchUrlState,
  options: {
    debouncedQ: string;
    debouncedAdvanced: MemberSearchAdvancedSlice;
    /**
     * SSN search term. Deliberately kept out of `MemberSearchUrlState` so it never
     * persists to the URL / browser history (PHI). Managed as local component state.
     */
    debouncedSsn?: string;
  },
): Omit<MemberSearchInput, 'page'> {
  const nameQuery = options.debouncedQ.trim() || undefined;

  const activeStatusQuery: MemberSearchInput['activeStatusQuery'] = state.activeStatus
    ? state.activeStatus
    : undefined;

  const adv = options.debouncedAdvanced;
  const firstName = adv.firstName.trim() || undefined;
  const lastName = adv.lastName.trim() || undefined;
  const email = adv.email.trim() || undefined;
  const zipCode = adv.zipCode.trim() || undefined;
  const phoneNumber = adv.phoneNumber.trim() || undefined;
  // When a group is pinned (exact `groupId`), `groupName` is only the banner label —
  // skip it as a filter so we don't redundantly AND a partial name match on top of
  // the exact group filter.
  const groupName = state.groupId ? undefined : adv.groupName.trim() || undefined;
  const groupId = state.groupId.trim() || undefined;
  // Only meaningful alongside a pinned group, which is the only thing it narrows.
  const externalTpa = groupId ? state.externalTpa.trim() || undefined : undefined;

  const dateOfBirth = adv.dateOfBirth.trim() || undefined;
  const ssn = options.debouncedSsn?.trim() || undefined;
  const effectiveStartFrom = adv.effectiveStartFrom.trim() || undefined;
  const effectiveStartTo = adv.effectiveStartTo.trim() || undefined;
  const effectiveEndFrom = adv.effectiveEndFrom.trim() || undefined;
  const effectiveEndTo = adv.effectiveEndTo.trim() || undefined;

  const advancedSearch: NonNullable<MemberSearchInput['advancedSearch']> = {};

  if (firstName) advancedSearch.firstName = firstName;
  if (lastName) advancedSearch.lastName = lastName;
  if (email) advancedSearch.email = email;
  if (zipCode) advancedSearch.zipCode = zipCode;
  if (phoneNumber) advancedSearch.phoneNumber = phoneNumber;
  if (groupName) advancedSearch.groupName = groupName;
  if (dateOfBirth) advancedSearch.dateOfBirth = dateOfBirth;
  if (ssn) advancedSearch.ssn = ssn;
  if (effectiveStartFrom) advancedSearch.effectiveStartFrom = effectiveStartFrom;
  if (effectiveStartTo) advancedSearch.effectiveStartTo = effectiveStartTo;
  if (effectiveEndFrom) advancedSearch.effectiveEndFrom = effectiveEndFrom;
  if (effectiveEndTo) advancedSearch.effectiveEndTo = effectiveEndTo;

  const hasAdvanced = Object.keys(advancedSearch).length > 0;

  return {
    nameQuery,
    activeStatusQuery,
    advancedSearch: hasAdvanced ? advancedSearch : undefined,
    sortKey: state.sortKey,
    sortDirection: state.sortDir,
    pageSize: MEMBER_SEARCH_PAGE_SIZE,
    relationship: state.relationship || undefined,
    clientId: state.clientId || undefined,
    excludeVip: state.excludeVip || undefined,
    // Tri-state on the API: true = has app account, false = no app account,
    // undefined = no filter.
    hasAppAccount: state.hasAppAccount ? true : state.notRegistered ? false : undefined,
    hasVerifiedEmail: state.hasVerifiedEmail || undefined,
    groupId,
    externalTpa,
  };
}

/**
 * Builds the global Member Search path pre-filtered to a single group. The
 * `groupId` (VBA Group_ID) drives the exact, access-checked backend filter;
 * `groupName` is carried only for the on-page "pinned group" banner label.
 */
export function buildGroupMemberSearchPath(groupId: string, groupName?: string): string {
  const params = new URLSearchParams({groupId});
  const trimmedName = groupName?.trim();
  if (trimmedName) params.set('groupName', trimmedName);
  return `/member-search?${params.toString()}`;
}

/**
 * The same path for a group administered by an external TPA.
 *
 * Both `externalTpa` and `groupId` are sent because a TPA group id is only unique
 * within one administrator — the id alone can name a group belonging to each of
 * them. `groupName` is the banner label only, as above.
 */
export function buildExternalTpaGroupMemberSearchPath(
  externalTpa: string,
  groupId: string,
  groupName?: string,
): string {
  const params = new URLSearchParams({groupId, externalTpa});
  const trimmedName = groupName?.trim();
  if (trimmedName) params.set('groupName', trimmedName);
  return `/member-search?${params.toString()}`;
}

export type MemberSearchUrlStateApi = {
  state: MemberSearchUrlState;
  update: (partial: Partial<MemberSearchUrlState>) => void;
};

export function useMemberSearchUrlState(): MemberSearchUrlStateApi {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseMemberSearchUrlState(searchParams), [searchParams]);

  // react-router v7's `setSearchParams` reference changes on every URL change
  // (its useCallback closes over `searchParams`). Stash it in a ref so the
  // returned `update` callback is stable across renders — otherwise effects
  // depending on `update` will spuriously re-fire on each navigation.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const update = useCallback((partial: Partial<MemberSearchUrlState>) => {
    setSearchParamsRef.current(
      (prev) => {
        const s = parseMemberSearchUrlState(prev);
        const next: MemberSearchUrlState = {...s, ...partial};
        return serializeMemberSearchUrlState(next);
      },
      {replace: true},
    );
  }, []);

  return {state, update};
}
